// Client for the user's access to the Spiris MCP and for decisions on paused Spiris runs.
// Mirrors webapi/Controllers/SpirisController.cs. Every call carries hostContext: availability is
// gated server-side on an eAccounting host, read from the AiaHostContext header. Every call throws on 429:
// an empty payload would read as a decision sent or a status received.
import { HostContext } from '../../embed-script/HostContext';
import { BackendServiceUrl, BaseService } from './BaseService';

export interface SpirisConnection {
    connected: boolean;
    status: string;
    // Why access is missing (signin_required | activation_required) when not connected.
    reason: string | null;
    // For activation_required: the configured Visma My Services base URL where the MCP is turned on.
    activationUrl?: string | null;
}

// Signed in with the right scope, but the company has not turned on the MCP / accepted its terms.
export const ActivationRequiredReason = 'activation_required';

export class SpirisService extends BaseService {
    constructor(accessToken: string, baseServiceUrl: string = BackendServiceUrl) {
        super(accessToken, baseServiceUrl);
    }

    public getConnectionAsync = async (hostContext?: HostContext): Promise<SpirisConnection> =>
        this.getResponseAsync<SpirisConnection>({
            commandPath: 'spiris/connection',
            method: 'GET',
            hostContext,
            throwOnRateLimit: true,
        });

    // Checks right now by reaching the MCP, clearing a remembered "not activated" state.
    public recheckConnectionAsync = async (hostContext?: HostContext): Promise<SpirisConnection> =>
        this.getResponseAsync<SpirisConnection>({
            commandPath: 'spiris/connection/recheck',
            method: 'POST',
            hostContext,
            throwOnRateLimit: true,
        });

    // 204 once the run has resumed; its answer arrives as a new bot message over SignalR, and the
    // card's status flip arrives as a message update.
    public decideApprovalAsync = async (
        chatId: string,
        approvalId: string,
        approved: boolean,
        hostContext?: HostContext,
    ): Promise<unknown> =>
        this.getResponseAsync({
            commandPath: `chats/${chatId}/spiris/approvals/${encodeURIComponent(approvalId)}`,
            method: 'POST',
            body: { approved },
            hostContext,
            throwOnRateLimit: true,
        });
}
